import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { goBack } from 'utils/history';
import { SortingCenter, SortingCenterUserRole, User } from 'types';
import CreateButton from 'components/Buttons/CreateButton';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { deleteSortingCenterUserRoles } from 'services/apiOperations';
import { notify } from 'components/Notifier';
import { AdministrationUserDisableUserRole } from 'components/Forms/AdministrationUserArrowTooltip';
import { AppContext } from 'context';
import CollapsableUserRoleListContainer from './CollapsableUserRoleListContainer';
import { AdministrationUserDialogDelete } from '../../../components/Dialogs/AdministrationUserDialogDelete';
import { AdministrationUserDialogLastDelete } from '../../../components/Dialogs/AdministrationUserDialogLastDelete';

type sortingCenterUserRolesListProps = {
  user: User;
};

const useStyles = makeStyles(theme => ({
  div: {
    display: 'inline-block',
  },
}));

const SortingCenterUserRolesList: React.FC<sortingCenterUserRolesListProps> = ({ user }) => {
  const context = useContext(AppContext);
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation('translation', { keyPrefix: 'administration.users.show.sortingCenterUserRolesList' });
  const classes = useStyles();

  const sortingCenterUserRoles: SortingCenterUserRole[] = user.sortingCenterUserRoles;

  const sortingCenterUserRoleGrouped: Array<Array<SortingCenterUserRole>> = [];
  const [selectedSortingCenterUserRole, setSelectedSortingCenterUserRole] = useState<SortingCenterUserRole | null>(
    null,
  );

  sortingCenterUserRoles.forEach((sortingCenterUserRole, index: number) => {
    const sortingCenter = sortingCenterUserRole.sortingCenter;
    const parentSortingCenterId = parseInt(transformObjectId(sortingCenter.parentSortingCenter), 10);
    if (parentSortingCenterId && !sortingCenterUserRoleGrouped[parentSortingCenterId]) {
      sortingCenterUserRoleGrouped[parentSortingCenterId] = [];
    }
    if (parentSortingCenterId) {
      sortingCenterUserRoleGrouped[parentSortingCenterId].push(sortingCenterUserRole);
    }
  });

  const linkTo = {
    pathname: `/administration/users/${transformObjectId(user)}/sorting-center-user-role`,
    state: { referer: location.pathname },
  };

  const sortingCenters = context.getSortingCenters?.();
  const eligibleSortingCenterChild: SortingCenter[] = [];
  sortingCenters?.forEach((sortingCenter: SortingCenter) => {
    sortingCenter.childrenSortingCenter?.forEach(sortingCenterChild => {
      eligibleSortingCenterChild.push(sortingCenterChild);
    });
  });

  const disableAddNewUserRoles = eligibleSortingCenterChild.length === sortingCenterUserRoles.length;

  const handleDelete = async () => {
    if (!selectedSortingCenterUserRole) return;
    try {
      await deleteSortingCenterUserRoles(transformObjectId(selectedSortingCenterUserRole));
      notify({
        message: t('deletion.success'),
        severity: 'success',
      });
      setSelectedSortingCenterUserRole(null);
      if (user.sortingCenterUserRoles.length === 1) {
        goBack(`/administration/users`, history);
      }
    } catch (e) {
      notify({
        message: t('errorGeneric'),
        severity: 'error',
      });
    }
  };

  return (
    <>
      <AdministrationUserDialogDelete
        isOpen={Boolean(selectedSortingCenterUserRole && user.sortingCenterUserRoles.length > 1)}
        setOpen={() => setSelectedSortingCenterUserRole(null)}
        sortingCenterUserRole={selectedSortingCenterUserRole}
        handleContinue={handleDelete}
      />
      <AdministrationUserDialogLastDelete
        isOpen={Boolean(selectedSortingCenterUserRole && user.sortingCenterUserRoles.length === 1)}
        setOpen={() => setSelectedSortingCenterUserRole(null)}
        handleContinue={handleDelete}
      />
      {sortingCenterUserRoleGrouped
        .sort(function (a: SortingCenterUserRole[], b: SortingCenterUserRole[]) {
          let codeA: string | undefined =
            a.length > 1 ? a[0].sortingCenter?.parentSortingCenter?.code : a[0].sortingCenter?.code;
          let codeB: string | undefined =
            b.length > 1 ? b[0].sortingCenter?.parentSortingCenter?.code : b[0].sortingCenter?.code;
          return codeA && codeB && codeA < codeB ? -1 : 1;
        })
        .map((sortingCenterUserRoleList: SortingCenterUserRole[], parentSortingCenterId) => (
          <CollapsableUserRoleListContainer
            key={parentSortingCenterId}
            parentSortingCenterId={parentSortingCenterId}
            sortingCenterUserRoleList={sortingCenterUserRoleList}
            setDialogSortingCenterUserRole={setSelectedSortingCenterUserRole}
            user={user}
          />
        ))}
      {disableAddNewUserRoles ? (
        <AdministrationUserDisableUserRole
          title={
            <>
              <p>{t('tooltipTitle')}</p>
              <p>{t('tooltipText')}</p>
            </>
          }
          placement="right"
          arrow
        >
          <div className={classes.div}>
            <CreateButton icon={null} label={t('addNewSortingCenterUserRoles')} disabled={disableAddNewUserRoles} />
          </div>
        </AdministrationUserDisableUserRole>
      ) : (
        <CreateButton icon={null} label={t('addNewSortingCenterUserRoles')} component={Link} to={linkTo} />
      )}
    </>
  );
};

export default SortingCenterUserRolesList;
